import { Office, OfficeDivision } from "@/models/office";
import { ChargerNames } from "@/models/chargerNames";
import { LoginModel, ResponseLogin, UserInfo } from "@/models/auth";
import axios, { AxiosResponse, AxiosError } from "axios";
import { ErrorResponse } from "@/common/CommonConstants";
import {
  ChargeCard,
  DriverWorkCycle,
  EvCarModel,
  GochargeCardWithDefaultStartDate,
  Subsidy,
  VehicleCode,
  PartnerAnnouncement,
  OutputControlCharger,
} from "@/models/common";
import qs from "qs";
import toast from "@/components/hoc/SnackbarAlert";
import { Messages } from "../Messages";

// エラー表示対象外のURL
const exclusionURL: RegExp[] = [
  /partnerAnnouncements\/$/,
  // tr_token をクエリパラメータとして付与するため、末尾一致だけでなく
  // クエリ文字列が続くケースも許容する
  /urlTrackingAccesses(\?|$)/,
];

export const httpClient = axios.create({
  baseURL: process.env.REACT_APP_PROD_URL || process.env.REACT_APP_DEV_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Add a request interceptor
httpClient.interceptors.request.use(
  function (config) {
    return config;
  },
  function (error) {
    return Promise.reject(error);
  }
);

// Add a response interceptor
httpClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError<ErrorResponse>) => {
    const errorStatus = error?.response?.status || 500;
    console.log("==error==", error, errorStatus);
    switch (errorStatus) {
      case 404:
      case 409:
        break;
      default: {
        const isExcluded = exclusionURL.some((reg) =>
          error.request.responseURL.match(reg)
        );
        if (!isExcluded) {
          toast.error(
            error?.response?.data?.message || Messages.CommonErrorMsg
          );
        }
        break;
      }
    }
    // Do something with response error
    return Promise.reject(error);
  }
);

export const loginAPI = async (
  data: LoginModel
): Promise<AxiosResponse<ResponseLogin>> => {
  return httpClient.post("core/internal/calgary/login", data);
};

export const getLoginUserAPI = async (): Promise<AxiosResponse<UserInfo>> => {
  return httpClient.get("core/internal/calgary/screen/");
};

export const getOfficesAPI = async (
  param: OfficeDivision
): Promise<AxiosResponse<Office[]>> => {
  return httpClient.get(`core/internal/calgary/offices/${param}`);
};

export const getChargerNamesAPI = async (
  officeId?: number
): Promise<AxiosResponse<ChargerNames[]>> => {
  return httpClient.get("core/internal/calgary/chargerNames/", {
    params: { officeId },
  });
};

export const getVehicleCodesAPI = async (
  officeIds?: number[]
): Promise<AxiosResponse<VehicleCode[]>> => {
  return httpClient.get("core/internal/calgary/vehicleCodes/", {
    params: { officeIds },
    paramsSerializer: (params) =>
      qs.stringify(params, { arrayFormat: "repeat", encode: false }),
  });
};

export const getEvCarModelsAPI = async (): Promise<
  AxiosResponse<EvCarModel[]>
> => {
  return httpClient.get("core/internal/calgary/evCarModels/");
};

export const getChargeCardsAPI = async (
  officeIds: number[]
): Promise<AxiosResponse<ChargeCard[]>> => {
  return httpClient.get("core/internal/calgary/chargeCards/", {
    params: { officeIds },
    paramsSerializer: (params) =>
      qs.stringify(params, { arrayFormat: "repeat", encode: false }),
  });
};

export const getSubsidiesAPI = async (): Promise<AxiosResponse<Subsidy[]>> => {
  return httpClient.get("core/internal/calgary/subsidies/");
};

export const getGochargeCardsAPI = async (
  officeId?: number
): Promise<AxiosResponse<GochargeCardWithDefaultStartDate[]>> => {
  return httpClient.get("core/internal/calgary/gochargeCards/", {
    params: { officeId },
  });
};

export const getDriverWorkCyclesAPI = async (
  officeIds: number[]
): Promise<AxiosResponse<DriverWorkCycle[]>> => {
  return httpClient.get("core/internal/calgary/driverWorkCycles/", {
    params: { officeIds },
    paramsSerializer: (params) =>
      qs.stringify(params, { arrayFormat: "repeat", encode: false }),
  });
};

export const getOfficeAPI = async (
  officeId: number
): Promise<AxiosResponse<Office>> => {
  return httpClient.get("/core/internal/calgary/offices/", {
    params: {
      officeId,
    },
  });
};

export const getPartnerAnnouncementsAPI = async (): Promise<
  AxiosResponse<PartnerAnnouncement[]>
> => {
  return httpClient.get("core/internal/calgary/partnerAnnouncements/");
};

export const getOutputControlChargersAPI = async (
  officeId: number
): Promise<AxiosResponse<OutputControlCharger>> => {
  return httpClient.get("core/internal/calgary/outputControlChargers", {
    params: {
      officeId,
    },
  });
};
